'use client'

import Image from 'next/image'
import Link from 'next/link'
import { Inter } from 'next/font/google'
import { useState, useEffect, useRef } from 'react'


const inter = Inter({ subsets: ['latin'] })

import MouseEffects from '@/components/MouseEffects'
import ScreenTransition from '@/components/ScreenTransition'
import CustomCursor from '@/components/CustomCursor'
import EnhancedMouseEffects from '@/components/EnhancedMouseEffects'
import { HoverCard, HoverButton, HoverText } from '@/components/HoverEffects'
import SectionTransition from '@/components/SectionTransition'
import { InteractiveCard } from '@/components/InteractiveCard'
import NewsSection from '@/components/NewsSection'

import Footer from '@/components/Footer'
import Navbar from '@/components/Navbar'
import DynamicPartnersSection from '@/components/DynamicPartnersSection'
import { motion } from 'framer-motion'

export default function Home() {
  const [currentImage, setCurrentImage] = useState(0)
  const [currentMobileCarouselIndex, setCurrentMobileCarouselIndex] = useState(0)
  const [applicationCategories, setApplicationCategories] = useState<any[]>([])
  const [carouselImages, setCarouselImages] = useState<any[]>([])
  const [mobileCarouselImages, setMobileCarouselImages] = useState<any[]>([])
  const [partnersData, setPartnersData] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [homepageSettings, setHomepageSettings] = useState<any>(null)
  const [systemSettings, setSystemSettings] = useState<any>({})
  
  // 图片放大相关状态 - 已移除弹窗功能
  // const [isImageZoomed, setIsImageZoomed] = useState(false)
  // const [zoomedImage, setZoomedImage] = useState<any>(null)
  // const [isCarouselPaused, setIsCarouselPaused] = useState(false)
  
  // 获取轮播图数据
  useEffect(() => {
    const fetchCarouselImages = async () => {
      try {
        const response = await fetch('/api/carousel')
        const result = await response.json()
        
        if (result.success && result.data.length > 0) {
          // 将数据库中的轮播图数据转换为前端需要的格式
          const images = result.data.map((item: any) => ({
            id: item.id,
            url: item.imageUrl,
            alt: item.description || '轮播图片'
          }))
          setCarouselImages(images)
        }
      } catch (err) {
        console.error('获取轮播图数据失败:', err)
      }
    }

    fetchCarouselImages()
  }, [])

  // 自动轮播效果（只在有轮播图时生效）
  useEffect(() => {
    if (carouselImages.length === 0) return
    
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % carouselImages.length)
    }, 5000) // 5秒自动切换

    return () => clearInterval(interval)
  }, [carouselImages.length])

  // 获取首页设置数据
  useEffect(() => {
    const fetchHomepageSettings = async () => {
      try {
        const response = await fetch('/api/homepage')
        const result = await response.json()
        
        if (result.success) {
          setHomepageSettings(result.data)
        }
      } catch (err) {
        console.error('获取首页设置失败:', err)
      }
    }

    fetchHomepageSettings()
  }, [])

  // 获取系统设置数据
  useEffect(() => {
    const fetchSystemSettings = async () => {
      try {
        const response = await fetch('/api/settings')
        const result = await response.json()
        
        if (result.success && result.data) {
          // 将系统设置转换为键值对格式
          const settingsMap: {[key: string]: string} = {}
          result.data.forEach((setting: any) => {
            settingsMap[setting.key] = setting.value
          })
          setSystemSettings(settingsMap)
        }
      } catch (err) {
        console.error('获取系统设置失败:', err)
      }
    }

    fetchSystemSettings()
  }, [])

  // 获取应用范围子栏目数据
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch('/api/categories')
        const result = await response.json()
        
        if (result.success && Array.isArray(result.data)) {
          // 筛选应用范围栏目（ID为2）下的子栏目，并按sortOrder排序
          const appCategories = result.data
            .filter((cat: any) => cat.parentId === 2 && cat.isActive)
            .sort((a: any, b: any) => a.sortOrder - b.sortOrder)
            .slice(0, 10) // 只取前10个
          
          setApplicationCategories(appCategories)
          setLoading(false)
        }
      } catch (err) {
        console.error('获取应用范围子栏目失败:', err)
        setError('网络请求失败')
        setLoading(false)
      }
    }

    fetchCategories()
  }, [])

  // 获取合作伙伴数据
  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const response = await fetch('/api/partners')
        const result = await response.json()
        
        if (result.success && Array.isArray(result.data)) {
          // 只显示启用的合作伙伴，并按sortOrder排序
          const activePartners = result.data
            .filter((partner: any) => partner.isActive)
            .sort((a: any, b: any) => a.sortOrder - b.sortOrder)
          setPartnersData(activePartners)
        }
      } catch (err) {
        console.error('获取合作伙伴数据失败:', err)
        // API失败时不设置任何数据
        setPartnersData([])
      }
    }

    fetchPartners()
  }, [])

  // 获取移动轮播图数据
  useEffect(() => {
    const fetchMobileCarouselImages = async () => {
      try {
        // 调用新闻API，获取栏目ID为33的新闻数据（与案例列表页保持一致）
        const response = await fetch('/api/news?categoryId=33&limit=100')
        const result = await response.json()
        
        console.log('新闻API返回数据:', result) // 调试信息
        
        // 处理数据结构 - 新闻API返回的是 { news: [], pagination: {} }
        let casesList = []
        
        if (result.news && Array.isArray(result.news)) {
          casesList = result.news
        } else if (Array.isArray(result)) {
          casesList = result
        }
        
        console.log('处理后的案例数据:', casesList) // 调试信息
        
        if (casesList.length > 0) {
          // 将新闻数据转换为移动轮播图格式
          const caseCarousels = casesList.map((caseItem: any, index: number) => ({
            id: caseItem.id,
            title: caseItem.title,
            imageUrl: caseItem.imageUrl || caseItem.image || '/api/placeholder/400/225',
            description: caseItem.summary || caseItem.content?.substring(0, 100) + '...',
            linkUrl: `/case/${caseItem.id}`,
            sortOrder: index,
            isActive: true,
            targetBlank: false
          }))
          
          console.log('转换后的轮播图数据:', caseCarousels) // 调试信息
          setMobileCarouselImages(caseCarousels)
        } else {
          console.log('API返回数据为空')
          setMobileCarouselImages([])
        }
      } catch (err) {
        console.error('获取新闻数据失败:', err)
        // API失败时不设置任何数据
        setMobileCarouselImages([])
      }
    }

    fetchMobileCarouselImages()
  }, [])

  // 刷新页面时自动回到顶部 - 使用更可靠的方法
  useEffect(() => {
    // 每次组件挂载时都滚动到顶部
    window.scrollTo({ top: 0, behavior: 'auto' })
    
    // 使用sessionStorage来检测页面刷新
    const handleBeforeUnload = () => {
      // 在页面卸载前设置标记
      sessionStorage.setItem('scrollToTopOnLoad', 'true')
    }
    
    const handleLoad = () => {
      // 检查是否需要滚动到顶部
      const shouldScroll = sessionStorage.getItem('scrollToTopOnLoad')
      if (shouldScroll === 'true') {
        // 延迟滚动以确保页面完全加载
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'auto' })
          // 清除标记
          sessionStorage.removeItem('scrollToTopOnLoad')
        }, 100)
      }
    }
    
    // 添加事件监听器
    window.addEventListener('beforeunload', handleBeforeUnload)
    window.addEventListener('load', handleLoad)
    
    // 处理页面刷新后的滚动
    const shouldScrollOnLoad = sessionStorage.getItem('scrollToTopOnLoad')
    if (shouldScrollOnLoad === 'true') {
      // 延迟滚动以确保DOM完全渲染
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'auto' })
        sessionStorage.removeItem('scrollToTopOnLoad')
      }, 200)
    }
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('load', handleLoad)
    }
  }, [])

  // 移动端轮播图自动滚动 - 使用CSS动画实现无限循环
  useEffect(() => {
    if (mobileCarouselImages.length === 0) return

    const interval = setInterval(() => {
      setCurrentMobileCarouselIndex(prev => {
        const nextIndex = prev + 1
        // 当滚动到倒数第三张图时，提前切换到第一张图，实现无缝循环（三张图显示）
        if (nextIndex >= mobileCarouselImages.length - 2) {
          // 触发动画重新渲染，实现无缝跳转
          setAnimationKey(prev => prev + 1)
          // 立即跳转到第一张图的位置（重置索引为0）
          return 0
        }
        return nextIndex
      })
    }, 3000) // 3秒自动切换

    return () => clearInterval(interval)
  }, [mobileCarouselImages.length])

  // 使用CSS动画实现真正的无限循环
  const [animationKey, setAnimationKey] = useState(0)

  // 移动轮播图左右移动函数 - 优化版本（三张图显示）
  const handleMobileCarouselPrev = () => {
    setCurrentMobileCarouselIndex(prev => {
      const prevIndex = prev - 1
      if (prevIndex < 0) {
        // 如果已经是第一张，跳转到最后一张
        return mobileCarouselImages.length - 1
      }
      return prevIndex
    })
  }

  const handleMobileCarouselNext = () => {
    setCurrentMobileCarouselIndex(prev => {
      const nextIndex = prev + 1
      if (nextIndex >= mobileCarouselImages.length) {
        // 如果已经是最后一张，跳转到第一张
        return 0
      }
      return nextIndex
    })
  }

  return (
    <>
      <CustomCursor />
      <MouseEffects />
      <EnhancedMouseEffects />
      <ScreenTransition>
        <main className="min-h-screen bg-black cursor-none">
      {/* 导航栏 */}
      <Navbar />

      {/* 英雄区 - 沉浸式深渊 */}
      <SectionTransition id="hero" className="relative h-[240px] md:h-[700px] w-full flex items-center justify-center overflow-hidden" immediate={true}>
        {/* 背景视频/动画 */}
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-orange-500/10 via-red-600/5 to-transparent"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,_var(--tw-gradient-stops))] from-red-600/10 via-transparent to-transparent"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,_var(--tw-gradient-stops))] from-orange-500/10 via-transparent to-transparent"></div>
          {/* 添加动态粒子效果 */}
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-orange-400 rounded-full animate-pulse"></div>
            <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-red-400 rounded-full animate-ping"></div>
            <div className="absolute bottom-1/3 left-1/3 w-1.5 h-1.5 bg-yellow-400 rounded-full animate-bounce"></div>
            <div className="absolute bottom-1/4 right-1/4 w-2 h-2 bg-orange-500 rounded-full animate-pulse" style={{animationDelay: '1s'}}></div>
          </div>
        </div>
        
        {/* 图片轮播区域 - 只在有轮播图时显示 */}
        {carouselImages.length > 0 ? (
          <div className="relative z-10 w-full flex items-center h-[240px] md:h-[700px] px-0">
            <div className="w-full">
              {/* 图片轮播 - 全屏展示 */}
                <div className="relative w-full mx-auto overflow-hidden group">
                {/* 轮播图片 */}
                  <div className="relative w-full h-[240px] md:h-[700px]">
                  {carouselImages.map((image, index) => (
                    <div
                      key={image.id}
                      className={`absolute inset-0 transition-opacity duration-3000 ${
                        index === currentImage ? 'opacity-100' : 'opacity-0'
                      }`}
                    >
                      <Image 
                        src={image.url}
                        alt={image.alt}
                        fill
                        className="object-contain md:object-cover w-full h-full"
                        priority={index === 0}
                        unoptimized
                      />
                    </div>
                  ))}
                </div>
                
                {/* 轮播指示器 */}
                <div className="absolute bottom-4 md:bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-2 md:space-x-3">
                  {carouselImages.map((_, index) => (
                    <button
                      key={index}
                      className={`w-2 h-2 md:w-3 md:h-3 rounded-full transition-all duration-300 cursor-pointer border-2 border-white/50 hover:border-white/80 focus:outline-none focus:ring-2 focus:ring-white/60 ${
                        index === currentImage ? 'bg-white scale-125 border-white' : 'bg-white/30 hover:bg-white/60'
                      }`}
                      onClick={() => setCurrentImage(index)}
                      aria-label={`切换到第${index + 1}张图片`}
                    >
                    </button>
                  ))}
                </div>
                
                {/* 左右切换按钮 */}
                <button
                  className="absolute left-2 md:left-4 top-1/2 transform -translate-y-1/2 w-8 h-8 md:w-12 md:h-12 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-all duration-300 hover:scale-110"
                  onClick={() => setCurrentImage((prev) => (prev - 1 + carouselImages.length) % carouselImages.length)}
                  aria-label="上一张图片"
                >
                  <svg className="w-4 h-4 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                
                <button
                  className="absolute right-2 md:right-4 top-1/2 transform -translate-y-1/2 w-8 h-8 md:w-12 md:h-12 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-all duration-300 hover:scale-110"
                  onClick={() => setCurrentImage((prev) => (prev + 1) % carouselImages.length)}
                  aria-label="下一张图片"
                >
                  <svg className="w-4 h-4 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

            </div>
          </div>
        ) : (
          // 没有轮播图时的占位内容
          <div className="relative z-10 w-full flex items-center justify-center h-[240px] md:h-[700px] px-0">
            <div className="text-center px-4">
              <h1 className="text-4xl md:text-6xl lg:text-8xl font-bold bg-gradient-to-r from-orange-500 via-red-600 to-yellow-500 bg-clip-text text-transparent mb-4 md:mb-6">
                超黑材料
              </h1>
              <p className="text-lg md:text-xl lg:text-2xl text-gray-300 max-w-2xl mx-auto">
                专业提供高性能超黑材料解决方案
              </p>
            </div>
          </div>
        )}

      </SectionTransition>

      {/* 产品展示 - 超黑材料应用 */}
      <SectionTransition id="product-showcase" className="py-32 bg-gradient-to-br from-gray-900 via-black to-gray-900 relative flex items-center">
        {/* 背景装饰 */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-red-600/5 rounded-full blur-3xl"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_70%,_var(--tw-gradient-stops))] from-orange-500/10 via-transparent to-transparent"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="">
            {/* 左侧文字内容 */}
            <div className="space-y-8">
              <div className="space-y-6 opacity-0 animate-fade-in-up" style={{animationDelay:'100ms'}}>
                <div className="flex flex-col items-center text-center space-y-4">
                  <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-600 rounded-full flex items-center justify-center">
                    <div className="w-4 h-4 bg-white rounded-full animate-pulse"></div>
                  </div>
                  <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                    {homepageSettings?.section2Title}
                  </h2>
                </div>
                <h3 className="text-2xl md:text-3xl font-light text-orange-400 text-center">
                 {homepageSettings?.section2Introduction}
                </h3>
              </div>
              
              <div 
                className="prose prose-invert max-w-none text-gray-300 leading-loose space-y-6 text-lg"
                dangerouslySetInnerHTML={{ __html: homepageSettings?.section2Content || '' }}
              />
              {/* 数据展示区域 */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
                <div className="bg-gradient-to-br from-orange-500/20 to-red-600/20 backdrop-blur-lg rounded-lg p-4 border border-orange-500/30 text-center group hover:scale-105 transition-all duration-300 opacity-0 animate-fade-in-up" style={{animationDelay: '600ms'}}>
                  <div className="text-2xl md:text-3xl font-bold text-orange-400 mb-1 group-hover:text-orange-300">{homepageSettings?.performanceValue1}</div>
                  <div className="text-white text-lg font-bold leading-tight tracking-wide">{homepageSettings?.performanceTitle1}</div>
                </div>
                
                <div className="bg-gradient-to-br from-blue-500/20 to-purple-600/20 backdrop-blur-lg rounded-lg p-4 border border-blue-500/30 text-center group hover:scale-105 transition-all duration-300 opacity-0 animate-fade-in-up" style={{animationDelay: '700ms'}}>
                  <div className="text-2xl md:text-3xl font-bold text-blue-400 mb-1 group-hover:text-blue-300">{homepageSettings?.performanceValue2}</div>
                  <div className="text-white text-lg font-bold leading-tight tracking-wide">{homepageSettings?.performanceTitle2}</div>
                </div>
                
                <div className="bg-gradient-to-br from-green-500/20 to-teal-600/20 backdrop-blur-lg rounded-lg p-4 border border-green-500/30 text-center group hover:scale-105 transition-all duration-300 opacity-0 animate-fade-in-up" style={{animationDelay: '800ms'}}>
                  <div className="text-2xl md:text-3xl font-bold text-green-400 mb-1 group-hover:text-green-300">{homepageSettings?.performanceValue3}</div>
                  <div className="text-white text-lg font-bold leading-tight tracking-wide">{homepageSettings?.performanceTitle3}</div>
                </div>
                
                <div className="bg-gradient-to-br from-yellow-500/20 to-orange-600/20 backdrop-blur-lg rounded-lg p-4 border border-yellow-500/30 text-center group hover:scale-105 transition-all duration-300 opacity-0 animate-fade-in-up" style={{animationDelay: '900ms'}}>
                  <div className="text-2xl md:text-3xl font-bold text-yellow-400 mb-1 group-hover:text-yellow-300">{homepageSettings?.performanceValue4}</div>
                  <div className="text-white text-lg font-bold leading-tight tracking-wide">{homepageSettings?.performanceTitle4}</div>
                </div>
              </div>
            </div>
            
            {/* 右侧企业形象和环境展示 */}
            <div className="relative opacity-0 animate-fade-in-up" style={{animationDelay: '1000ms'}}>
            </div>
          </div>
        </div>
      </SectionTransition>

      {/* 终极黑漆 - 眼见为实 */}
      <SectionTransition id="ultimate-black" className="py-8 md:py-0 bg-black relative overflow-hidden">
        {/* 对角线光效 - 与第一屏保持一致 */}
        <div className="absolute inset-0">
          {/* 主对角线渐变 - 橙紫色调 */}
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 via-purple-600/5 to-transparent"></div>
          
          {/* 辅助对角线渐变 - 蓝粉色调 */}
          <div className="absolute inset-0 bg-gradient-to-bl from-blue-500/8 via-pink-500/3 to-transparent"></div>
          
          {/* 中心光晕效果 */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-1/2 h-1/2 bg-gradient-to-r from-orange-500/5 via-purple-600/3 to-orange-500/5 rounded-full blur-3xl"></div>
          
          {/* 动态光点效果 */}
          <div className="absolute inset-0">
            {[
              {left: 58.62, top: 26.34, duration: 5.59, delay: 0.70},
              {left: 57.47, top: 5.85, duration: 3.24, delay: 0.81},
              {left: 77.17, top: 98.74, duration: 4.48, delay: 2.21},
              {left: 77.69, top: 43.96, duration: 5.62, delay: 2.42},
              {left: 41.08, top: 80.48, duration: 3.89, delay: 0.87},
              {left: 89.56, top: 40.26, duration: 4.04, delay: 0.53},
              {left: 22.80, top: 67.21, duration: 6.31, delay: 2.52},
              {left: 76.73, top: 88.45, duration: 5.78, delay: 1.89},
              {left: 82.97, top: 27.20, duration: 3.92, delay: 2.57},
              {left: 22.01, top: 7.00, duration: 3.73, delay: 1.33},
              {left: 37.76, top: 6.20, duration: 4.44, delay: 1.80},
              {left: 30.70, top: 40.24, duration: 6.28, delay: 2.74},
              {left: 54.38, top: 0.74, duration: 3.51, delay: 0.09},
              {left: 48.71, top: 46.74, duration: 6.97, delay: 0.16},
              {left: 26.98, top: 28.72, duration: 3.04, delay: 2.28},
              {left: 93.06, top: 11.57, duration: 4.73, delay: 2.58},
              {left: 76.41, top: 55.45, duration: 5.85, delay: 1.79},
              {left: 5.37, top: 96.94, duration: 5.30, delay: 2.11},
              {left: 99.23, top: 4.17, duration: 5.52, delay: 1.69},
              {left: 13.73, top: 71.01, duration: 5.37, delay: 1.33},
              {left: 37.85, top: 61.07, duration: 3.25, delay: 1.38},
              {left: 52.12, top: 35.84, duration: 3.95, delay: 1.26},
              {left: 7.12, top: 81.98, duration: 6.35, delay: 0.01},
              {left: 35.36, top: 9.65, duration: 4.10, delay: 2.70},
              {left: 26.93, top: 28.22, duration: 4.25, delay: 1.62},
              {left: 52.40, top: 57.50, duration: 6.91, delay: 0.04},
              {left: 23.17, top: 33.47, duration: 3.82, delay: 1.39},
              {left: 15.24, top: 58.12, duration: 4.83, delay: 0.96},
              {left: 4.20, top: 59.91, duration: 3.30, delay: 0.41},
              {left: 79.80, top: 3.93, duration: 5.99, delay: 0.11}
            ].map((point, i) => (
              <div
                key={i}
                className="absolute w-1 h-1 bg-white rounded-full"
                style={{
                  left: `${point.left}%`,
                  top: `${point.top}%`,
                  animation: `twinkle ${point.duration}s ease-in-out infinite`,
                  animationDelay: `${point.delay}s`,
                }}
              />
            ))}
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-8 md:py-20">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6 text-white">
            {homepageSettings?.section3Title}
            </h2>
            <p className="text-base md:text-lg text-gray-300 max-w-4xl mx-auto leading-relaxed mb-4 md:mb-8 px-4">
             {homepageSettings?.section3Introduction}
            </p>
            
            {/* 应用范围子栏目链接 */}
            {!loading && !error && applicationCategories.length > 0 && (
              <div className="max-w-[1200px] mx-auto">
                <div className="flex flex-wrap gap-3 justify-center">
                  {applicationCategories.map((category) => (
                    <Link 
                      key={category.id}
                      href={`/applications/${category.slug}`}
                      className="px-5 py-2 rounded-full font-semibold bg-gray-800 text-gray-300 hover:bg-gray-700 transition-all duration-300 whitespace-nowrap"
                    >
                      {category.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* 产品展示网格 - 动态渲染应用范围子栏目 - 修改为文字在上，图片在下 */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 md:gap-6">
            {loading ? (
              // 加载状态
              <div className="col-span-2 text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
                <p className="mt-4 text-gray-400">加载中...</p>
              </div>
            ) : error ? (
              // 错误状态
              <div className="col-span-2 text-center py-12">
                <div className="text-red-500 text-2xl mb-4">⚠️</div>
                <p className="text-red-400">{error}</p>
                <button 
                  onClick={() => window.location.reload()}
                  className="mt-4 px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors duration-300"
                >
                  重新加载
                </button>
              </div>
            ) : applicationCategories.length === 0 ? (
              // 空数据状态
              <div className="col-span-2 text-center py-12">
                <div className="text-gray-500 text-2xl mb-4">📂</div>
                <p className="text-gray-400">暂无应用范围数据</p>
              </div>
            ) : (
              // 动态渲染应用范围子栏目 - 修改布局：文字在上，图片在下
              applicationCategories.map((category, index) => {
                // 颜色数组，保持原有的hover颜色效果
                const colors = [
                  { gradient: 'from-orange-500/10 via-red-600/5', text: 'orange-400' },
                  { gradient: 'from-blue-500/10 via-purple-600/5', text: 'blue-400' },
                  { gradient: 'from-green-500/10 via-teal-600/5', text: 'green-400' },
                  { gradient: 'from-yellow-500/10 via-orange-600/5', text: 'yellow-400' },
                  { gradient: 'from-purple-500/10 via-pink-600/5', text: 'purple-400' },
                  { gradient: 'from-red-500/10 via-rose-600/5', text: 'red-400' },
                  { gradient: 'from-indigo-500/10 via-blue-600/5', text: 'indigo-400' },
                  { gradient: 'from-teal-500/10 via-cyan-600/5', text: 'teal-400' },
                  { gradient: 'from-pink-500/10 via-rose-600/5', text: 'pink-400' },
                  { gradient: 'from-amber-500/10 via-yellow-600/5', text: 'amber-400' }
                ];
                
                const color = colors[index % colors.length];
                
                return (
                  <Link key={category.id} href={`/applications/${category.slug}`} className="group relative bg-gradient-to-br from-gray-900 via-black to-gray-800 rounded-2xl overflow-hidden border border-white/10 hover:border-orange-500/30 transition-all duration-500 hover:scale-105">
                    <div className={`absolute inset-0 bg-gradient-to-br ${color.gradient} to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
                    
                    {/* 文字内容 - 放在上方 */}
                    <div className="p-6 text-center">
                      <h3 className={`text-2xl font-bold text-white mb-3 group-hover:text-${color.text} transition-all duration-300 transform group-hover:scale-110`}>
                        {category.name}
                      </h3>
                      <p className="text-gray-200 text-sm leading-relaxed">
                        {category.listDisplayText || '暂无介绍'}
                      </p>
                    </div>
                    
                    {/* 图片 - 放在下方 */}
                    <div className="relative aspect-video bg-black/50 flex items-center justify-center">
                      <img 
                        src={category.coverImage || "/api/placeholder/600/400"}
                        alt={category.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    </div>
                  </Link>
                );
              })
            )}
          </div>
        </div>
      </SectionTransition>

      {/* 移动轮播图 - 重写版本 */}
      <SectionTransition className="py-16 bg-black relative overflow-hidden">
        <div className="w-full px-4 sm:px-6 lg:px-8">          {/* 板块标题和描述 - 参考第二屏样式 */}
          <div className="mb-16 w-[1200px] mx-auto">
            <div className="flex flex-col items-center text-center space-y-4 mb-8">
              <div className="w-16 h-16 rounded-full flex items-center justify-center">
                 <img 
                   src={homepageSettings?.siteLogo} 
                   alt="网站Logo"
                   className="w-full h-full object-contain"
                   onError={(e) => {
                     (e.target as HTMLImageElement).src = '/api/placeholder/64/64';
                   }}
                 />
               </div>
              <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                {homepageSettings?.caseTitle}
              </h2>
            </div>
           <h3 className="text-2xl md:text-3xl font-light text-orange-400 text-center mb-8"> {homepageSettings?.caseDescription} </h3>
            <div 
              className="prose prose-invert max-w-none text-gray-300 leading-relaxed space-y-6 text-xl"
              dangerouslySetInnerHTML={{ __html: homepageSettings?.caseContent || '' }}
            />
          </div>
         
          {/* 移动轮播图容器 - 水平滚动布局 */}
          <div className="relative w-full overflow-hidden">
            <div className="relative h-[20rem] md:h-[30rem]">
              
              {/* 左移动按钮 */}
              {mobileCarouselImages.length > 0 && (
                <button
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 w-10 h-10 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center text-white transition-all duration-300 hover:scale-110 backdrop-blur-sm border border-white/20"
                  onClick={handleMobileCarouselPrev}
                  aria-label="上一张轮播图"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
              
              {/* 右移动按钮 */}
              {mobileCarouselImages.length > 0 && (
                <button
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 w-10 h-10 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center text-white transition-all duration-300 hover:scale-110 backdrop-blur-sm border border-white/20"
                  onClick={handleMobileCarouselNext}
                  aria-label="下一张轮播图"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}
              {mobileCarouselImages.length > 0 ? (
                <div 
                  key={animationKey}
                  className="flex transition-transform duration-500 ease-in-out mobile-carousel-container"
                  style={{ 
                    transform: `translateX(-${currentMobileCarouselIndex * (typeof window !== 'undefined' && window.innerWidth < 768 ? 100 : 33.33)}%)`
                  }}
                >
                  {/* 实现真正的无缝循环 - 渲染图片+第一张图片的副本 */}
                  {mobileCarouselImages.map((carousel, index) => (
                    <a
                      key={`${carousel.id}-${index}`}
                      href={carousel.linkUrl}
                      className="flex-shrink-0 w-full md:w-1/3 px-4 md:px-6 py-8 block mobile-carousel-item"
                    >
                      <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-white/10 shadow-xl group hover:border-orange-400/50 transition-all duration-300 cursor-pointer">
                        {/* 文字区域 - 放在图片上方 */}
                        <div className="p-4 text-center">
                          <h3 className="text-white font-bold text-2xl mb-3 group-hover:text-orange-400 transition-all duration-300 transform group-hover:scale-110">{carousel.title}</h3>
                          {carousel.description && (
                            <p className="text-gray-200 text-sm leading-relaxed group-hover:text-gray-200 transition-colors duration-300">{carousel.description}</p>
                          )}
                        </div>
                        
                        {/* 图片区域 - 调整高度显示合适内容 */}
                        <div className="relative w-full h-48 md:h-80 overflow-hidden">
                            <Image 
                              src={carousel.imageUrl}
                              alt={carousel.title || '移动轮播图片'}
                              fill
                              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                              className="object-cover hover:opacity-90 transition-opacity duration-300"
                              placeholder="blur"
                              blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAAIAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECIgDRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pV2HaUMkK0XeU0krUwtwSqBcX2Y//Z"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = '/api/placeholder/400/256';
                              }}
                            />
                        </div>
                      </div>
                    </a>
                  ))}
                  

                </div>
              ) : (
                <div className="flex items-center justify-center h-full">

                </div>
              )}
            </div>
            

            
            {/* 装饰元素 */}
            <div className="absolute -top-4 -left-4 w-8 h-8 bg-orange-500 rounded-full opacity-70 animate-pulse"></div>
            <div className="absolute -bottom-4 -right-4 w-6 h-6 bg-blue-500 rounded-full opacity-70 animate-bounce"></div>
            <div className="absolute top-1/2 -right-8 w-4 h-4 bg-green-500 rounded-full opacity-60 animate-ping"></div>
          </div>
        </div>
      </SectionTransition>
      {/* 图片放大模态框 - 移到SectionTransition外部 */}
      {/* 已移除弹窗功能，改为直接链接访问 */}

      {/* 合作方 - 星河共舞 */}
      <SectionTransition id="partners" className="py-20 bg-gray-900 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
             {homepageSettings?.section4Title}
            </h2>
            <p className="text-xl text-gray-400">
              {homepageSettings?.section4Introduction}
            </p>
          </div>
          
          {/* 合作伙伴展示区域 */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {partnersData.length > 0 ? (
              partnersData.map((partner: any, index) => (
                <div key={partner.id} className="bg-black/30 rounded-xl p-6 backdrop-blur-sm border border-white/10 transition-all duration-500 hover:-translate-y-2 cursor-pointer">
                  <div className="aspect-[16/9] flex items-center justify-center relative overflow-hidden">
                    <img 
                      src={partner.logo || '/api/placeholder/300/169'} 
                      alt={partner.name}
                      className="w-full h-full object-contain p-0"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = '/api/placeholder/300/169';
                      }}
                    />
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <p className="text-gray-400 text-lg">暂无合作伙伴数据</p>
                <p className="text-gray-500 text-sm mt-2">请在后台上传合作伙伴信息</p>
              </div>
            )}
          </div>
        </div>
      </SectionTransition>

      {/* 合作伙伴下方图片展示 - 独立满屏板块 */}
      <SectionTransition className="relative">
        <div className="w-full h-[440px]">
          <img 
            src={homepageSettings?.section5Image}
            alt="银河合作伙伴展示"
            className="object-cover w-full h-full"
          />
        </div>
      </SectionTransition>

      {/* 应用领域 - 创世之暗 */}
      <SectionTransition id="applications" className="py-20 bg-black relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
            {homepageSettings?.section6Title}
            </h2>
            <p className="text-xl text-gray-400">
            {homepageSettings?.section6Introduction}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-blue-900/30 to-purple-900/30 rounded-2xl p-8 backdrop-blur-sm border border-white/10 hover:border-blue-400/50 hover:shadow-2xl hover:shadow-blue-500/20 transition-all duration-500 group hover:-translate-y-2 cursor-pointer">
              <div className="text-center mb-6">
                <div className="text-3xl font-bold text-blue-400 mb-2 group-hover:text-blue-300 transition-colors duration-300">{homepageSettings?.applicationCard1Title}</div>
                <div className="text-gray-400 group-hover:text-gray-300 transition-colors duration-300">{homepageSettings?.applicationCard1Subtitle}</div>
              </div>
              <p className="text-gray-300 text-center group-hover:text-white transition-colors duration-300 leading-relaxed">
                {homepageSettings?.applicationCard1Description}
              </p>
            </div>
            <div className="bg-gradient-to-br from-green-900/30 to-teal-900/30 rounded-2xl p-8 backdrop-blur-sm border border-white/10 hover:border-green-400/50 hover:shadow-2xl hover:shadow-green-500/20 transition-all duration-500 group hover:-translate-y-2 cursor-pointer">
              <div className="text-center mb-6">
                <div className="text-3xl font-bold text-green-400 mb-2 group-hover:text-green-300 transition-colors duration-300">{homepageSettings?.applicationCard2Title}</div>
                <div className="text-gray-400 group-hover:text-gray-300 transition-colors duration-300">{homepageSettings?.applicationCard2Subtitle}</div>
              </div>
              <p className="text-gray-300 text-center group-hover:text-white transition-colors duration-300 leading-relaxed">
                {homepageSettings?.applicationCard2Description}
              </p>
            </div>
            <div className="bg-gradient-to-br from-pink-900/30 to-rose-900/30 rounded-2xl p-8 backdrop-blur-sm border border-white/10 hover:border-pink-400/50 hover:shadow-2xl hover:shadow-pink-500/20 transition-all duration-500 group hover:-translate-y-2 cursor-pointer">
              <div className="text-center mb-6">
                <div className="text-3xl font-bold text-pink-400 mb-2 group-hover:text-pink-300 transition-colors duration-300">{homepageSettings?.applicationCard3Title}</div>
                <div className="text-gray-400 group-hover:text-gray-300 transition-colors duration-300">{homepageSettings?.applicationCard3Subtitle}</div>
              </div>
              <p className="text-gray-300 text-center group-hover:text-white transition-colors duration-300 leading-relaxed">
                {homepageSettings?.applicationCard3Description}
              </p>
            </div>
          </div>
          
          {/* 感谢社群 - 新增的美丽文字区域 */}
          <motion.div 
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
            viewport={{ once: true }}
            className="mt-20 text-center group"
            whileHover={{ scale: 1.02 }}
          >
            <div className="max-w-7xl mx-auto">
              <div className="bg-gradient-to-r from-orange-500/10 via-red-500/10 to-orange-500/10 rounded-3xl p-8 md:p-12 border border-white/20 backdrop-blur-sm 
                           group-hover:from-orange-500/20 group-hover:via-red-500/20 group-hover:to-orange-500/20 
                           group-hover:border-orange-400/30 group-hover:shadow-2xl group-hover:shadow-orange-500/20 
                           transition-all duration-500 transform">
                <motion.div
                  initial={{ scale: 0.9 }}
                  whileInView={{ scale: 1 }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.05 }}
                >
                  <motion.h3 
                    className="text-2xl md:text-3xl font-bold mb-6 bg-gradient-to-r from-orange-400 via-red-400 to-orange-400 bg-clip-text text-transparent 
                             group-hover:from-orange-300 group-hover:via-red-300 group-hover:to-orange-300 
                             transition-all duration-300"
                    whileHover={{ scale: 1.1 }}
                    transition={{ type: "spring", stiffness: 400, damping: 10 }}
                  >
                    {homepageSettings?.communityTitle || '致敬艺术创造者'}
                  </motion.h3>
                  <motion.div 
                    className="text-lg md:text-xl text-gray-300 leading-relaxed mb-4 whitespace-nowrap 
                             group-hover:text-white group-hover:scale-105 
                             transition-all duration-300"
                    whileHover={{ y: -2 }}
                  >
                    {homepageSettings?.communityDescription1}
                   
                  <motion.div 
                    className="text-base md:text-lg text-gray-400 leading-relaxed whitespace-nowrap 
                             group-hover:text-gray-300 group-hover:scale-105 
                             transition-all duration-300"
                    whileHover={{ y: -2 }}
                  >
                  {homepageSettings?.communityDescription2}
                  </motion.div>
                  </motion.div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </SectionTransition>

      {/* 最新动态 - 行业前沿 */}
      <NewsSection />

      {/* 终极召唤 - 触摸虚无 */}
      <SectionTransition id="cta" className="py-20 bg-gray-900 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
            {homepageSettings?.ctaTitle}
          </h2>
          <p className="text-xl text-gray-400 mb-12 max-w-3xl mx-auto">
            {homepageSettings?.ctaDescription}
          </p>
          {systemSettings.header_footer_links ? (
            <Link href={systemSettings.header_footer_links} target="_blank" rel="noopener noreferrer">
              <HoverButton className="px-12 py-6 rounded-xl text-xl font-bold animate-pulse">
                申请尊享样品
              </HoverButton>
            </Link>
          ) : (
            <HoverButton className="px-12 py-6 rounded-xl text-xl font-bold animate-pulse">
              申请尊享样品
            </HoverButton>
          )}
        </div>
      </SectionTransition>

        {/* 页脚 */}
        <Footer />

    </main>
  </ScreenTransition>
</>
)
}
